import React from "react";
import { MessageSquare, Mic, Phone, Sparkles } from "lucide-react";
import { WIDGET_THEMES } from "../../widget/themes";

const ALL_CHANNELS = [
  { id: "web_chat", label: "Web Chat", Icon: MessageSquare },
  { id: "web_voice", label: "Web Voice", Icon: Mic },
];

export default function ChannelsColumn({ agent, setAgent, voices }) {
  const channels = agent.channels?.length ? agent.channels : [agent.type || "web_chat"];
  const enabled = (c) => channels.includes(c);
  const toggle = (c) => {
    const next = enabled(c) ? channels.filter((x) => x !== c) : [...channels, c];
    setAgent({ ...agent, channels: next, type: next[0] || "web_chat" });
  };

  return (
    <div className="space-y-4" data-testid="builder-col-channels">
      <div className="flex items-center gap-2 pb-2 border-b-2 border-[#030712]">
        <div className="font-display font-extrabold text-lg uppercase tracking-tight">Channels</div>
        <div className="text-[10px] font-mono text-[#4B5563]">how customers reach you</div>
      </div>

      <div className="hs-card p-4">
        <label className="text-[10px] font-mono uppercase tracking-wide text-[#4B5563]">Activate channels</label>
        <div className="text-[11px] text-[#4B5563] mt-1">Same prompt + knowledge across selected channels.</div>
        <div className="grid grid-cols-2 gap-2 mt-3">
          {ALL_CHANNELS.map(({ id, label, Icon }) => {
            const on = enabled(id);
            return (
              <button
                key={id}
                data-testid={`channel-toggle-${id}`}
                onClick={() => toggle(id)}
                className={`border p-3 text-xs flex flex-col items-center gap-1 transition-colors ${on ? "border-[#0044FF] bg-[#0044FF]/5 text-[#0044FF]" : "border-[#E2E8F0] text-[#4B5563] hover:border-[#030712]"}`}
              >
                <Icon className="w-5 h-5" /> {label}
                <span className="text-[9px] font-mono">{on ? "ON" : "OFF"}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="hs-card p-4">
        <label className="text-[10px] font-mono uppercase tracking-wide text-[#4B5563]">Identity</label>
        <input data-testid="builder-name-input" value={agent.name} onChange={(e) => setAgent({ ...agent, name: e.target.value })} className="w-full border-b border-[#E2E8F0] py-2 text-base font-display font-bold outline-none focus:border-[#0044FF]" />
        <input data-testid="builder-description-input" value={agent.description || ""} onChange={(e) => setAgent({ ...agent, description: e.target.value })} placeholder="Description" className="w-full border-b border-[#E2E8F0] py-2 text-xs text-[#4B5563] outline-none mt-1 focus:border-[#0044FF]" />
      </div>

      <div className="hs-card p-4">
        <div className="flex items-center justify-between">
          <label className="text-[10px] font-mono uppercase tracking-wide text-[#4B5563]">System Prompt</label>
          <Sparkles className="w-3.5 h-3.5 text-[#0044FF]" />
        </div>
        <textarea data-testid="builder-prompt-input" rows={7} value={agent.system_prompt} onChange={(e) => setAgent({ ...agent, system_prompt: e.target.value })} className="w-full border border-[#E2E8F0] mt-2 p-3 rounded-sm font-mono text-[11px] leading-relaxed" />
      </div>

      <div className="hs-card p-4">
        <label className="text-[10px] font-mono uppercase tracking-wide text-[#4B5563]">Welcome message</label>
        <input data-testid="builder-welcome-input" value={agent.welcome_message || ""} onChange={(e) => setAgent({ ...agent, welcome_message: e.target.value })} className="w-full border border-[#E2E8F0] mt-2 p-2 rounded-sm text-xs" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="hs-card p-3">
          <label className="text-[10px] font-mono uppercase tracking-wide text-[#4B5563]">Voice</label>
          <select data-testid="builder-voice-select" value={agent.voice_id} onChange={(e) => setAgent({ ...agent, voice_id: e.target.value })} className="w-full border border-[#E2E8F0] mt-1 p-2 rounded-sm bg-white text-xs">
            {voices.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.gender})</option>)}
          </select>
        </div>
        <div className="hs-card p-3">
          <label className="text-[10px] font-mono uppercase tracking-wide text-[#4B5563]">Temperature</label>
          <input data-testid="builder-temperature-input" type="number" min={0} max={1} step={0.1} value={agent.temperature} onChange={(e) => setAgent({ ...agent, temperature: parseFloat(e.target.value) })} className="w-full border border-[#E2E8F0] mt-1 p-2 rounded-sm text-xs" />
        </div>
      </div>

      <div className="hs-card p-3">
        <label className="text-[10px] font-mono uppercase tracking-wide text-[#4B5563]">Default language</label>
        <select
          data-testid="builder-language-select"
          value={agent.language || "English"}
          onChange={(e) => setAgent({ ...agent, language: e.target.value })}
          className="w-full border border-[#E2E8F0] mt-1 p-2 rounded-sm bg-white text-xs"
        >
          {[
            "English","Spanish","French","German","Italian","Portuguese","Dutch",
            "Hindi","Urdu","Arabic","Turkish","Russian","Polish","Greek",
            "Chinese","Japanese","Korean","Indonesian","Vietnamese","Thai","Filipino",
            "Bengali","Tamil","Telugu","Marathi","Punjabi","Gujarati",
            "Swedish","Norwegian","Danish","Finnish","Czech","Hungarian","Romanian","Hebrew","Ukrainian",
          ].map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <div className="text-[10px] text-[#4B5563] mt-1">Used for chat replies, voice responses & phone calls. Customers can switch at any time.</div>
      </div>

      <div className="hs-card p-3">
        <label className="text-[10px] font-mono uppercase tracking-wide text-[#4B5563]">Widget color</label>
        <div className="text-[11px] text-[#4B5563] mt-1">Pick the look that matches your brand. Applies to the chat bubble, buttons, and launcher.</div>
        <div className="grid grid-cols-4 gap-2 mt-3" data-testid="theme-grid">
          {Object.entries(WIDGET_THEMES).map(([key, t]) => {
            const active = (agent.theme || "blue") === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setAgent({ ...agent, theme: key })}
                data-testid={`theme-option-${key}`}
                className={`relative aspect-square rounded-sm flex items-end p-1.5 text-[9px] font-mono uppercase ${active ? "ring-2 ring-offset-1 ring-[#030712]" : "hover:ring-1 hover:ring-[#0F1115]/30"}`}
                style={{ background: t.primary, color: t.ink }}
                title={t.name}
              >
                {active && <div className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-white shadow" />}
                {t.name}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
